<script setup>
import { uniqBy } from 'lodash-es';
import { onMounted, onUnmounted, ref, watch } from 'vue';
import { useCommonImports } from '~/common/composables/common-imports.composable.js';
import { useMapItemCreation } from '~/common/composables/mapbox/creation-icons';
import { useMap } from '~/common/composables/mapbox/maps';
import { useMapboxFeatureSelection } from '~/common/composables/mapbox/selection';
import { parseStringifiedObjectsInArray } from '~/common/utils/common.utils';
import ThermNewForm from '~/forms/components/new-form/therm-new-form.vue';
import { useFormsStore } from '~/forms/store/forms.store';
import TaskForm from '~/tasks/components/molecule/task-form/therm-task-form.vue';
import { useTasksStore } from '~/tasks/store/tasks.store';
import { useThermHelperComposable } from '~/therm/composables/helper-composable.js';
import ThermLayers from '../components/therm-layers.vue';
import ThermProjects from '../components/therm-projects.vue';
import ThermRightSidebar from '../components/therm-right-sidebar.vue';
import ThermTools from '../components/therm-tools.vue';
import { useThermStore } from '../store/therm.store';

const {
  $t,
  $toast,
  route,
  router,
} = useCommonImports();
const therm_store = useThermStore();
const tasks_store = useTasksStore('therm_tasks_store');
const forms_store = useFormsStore('therm_forms_store');
const { addSymbols, loadTaskIcons, loadFormIcons, createThermTasks, loadDefectStatusIcons } = useThermHelperComposable();
const task_marker_active = ref(false);
const form_marker_active = ref(false);
const open_task_popup = ref(false);
const open_form_popup = ref(false);
const active_tool = ref('');
const task_form_centroid = ref(null);

watch(() => [
  tasks_store.tasks(),
  forms_store.forms,
  therm_store.use_tasks,
  therm_store.show_defect_status_icons,
  therm_store.features,
  therm_store.gallery_view_state.is_active,
], () => {
  addSymbols();
});
watch(() => therm_store.last_selected_project, async () => {
  await initial_project_initialization();
});
const { styleSelection, loadSelectionEvents } = useMapboxFeatureSelection({

  layer_ids: ['polygon_feature_layer', 'linestring_feature_layer', 'point_feature_layer'],
}, (e) => {
  therm_store.selected_features = parseStringifiedObjectsInArray(uniqBy(e, f => f.properties.uid).map(item => ({
    geometry: item.geometry || item._geometry,
    id: item.id,
    properties: item.properties,
    type: item.type,
  })));
});
const { loadSelectionEvents: loadTaskFormSelectionEvents } = useMapboxFeatureSelection({
  layer_ids: ['symbol-layer-icon'],
  style_selection: false,
  use_normal_selection: true,
}, (e, type) => {
  const tasksForms = uniqBy(e, f => f.properties.uid).map(item => ({
    geometry: item.geometry || item._geometry,
    id: item.id,
    properties: item.properties,
    type: item.type,
  }));

  if (tasksForms.length === 1 && type === 'click') {
    const task_form = tasksForms[0].properties;
    logger.log(tasksForms);
    if (task_form.feature_type === 'task') {
      router.push({
        ...route,
        query: {
          task: btoa(JSON.stringify({
            id: task_form.feature_type_id,
            store_key: 'therm_tasks_store',
          })),
        },
      });
    }
    else if (task_form.feature_type === 'form') {
      router.push({
        ...route,
        query: {
          form: btoa(JSON.stringify({
            form_uid: task_form.feature_type_id,
            store_key: 'therm_forms_store',
          })),
        },
      });
    }
  }
});
watch(() => therm_store.selected_features, (val) => {
  if (val) {
    styleSelection(val, {
      mapbox_instance: therm_store.map,
      colors_map_accessor: 'featureTypeId',
      colors_map: therm_store.ftg,
    });
  }
});

const { initMapbox, loadMapBoxPackage, addMapboxToken, setSources, setLayers, removeMapboxInstance } = useMap({}, async (event_data, event_name) => {
  if (event_name === 'loaded') {
    loadTaskIcons();
    loadFormIcons();
    loadSelectionEvents({ mapbox_instance: therm_store.map, use_gl_draw: false });
    loadTaskFormSelectionEvents({ mapbox_instance: therm_store.map, use_gl_draw: false });
    const sources = ['all_features_source', 'symbol-source'];

    const layers = [
      {
        id: 'point_feature_layer',
        type: 'circle',
        source: 'all_features_source',
        filter: ['==', '$type', 'Point'],
      },
      {
        id: 'polygon_feature_layer',
        type: 'fill',
        source: 'all_features_source',
        paint: {
          'fill-opacity': 0,
        },
      },
      {
        id: 'linestring_feature_layer',
        type: 'line',
        source: 'all_features_source',
        paint: {
          'line-width': 2,
        },
      },
      {
        id: 'symbol-layer-icon',
        type: 'symbol',

        source: 'symbol-source',
        layout: {
          'icon-image': ['coalesce', ['get', 'icon'], 'marker'],
          'icon-size': ['coalesce', ['get', 'icon_size'], 1],
          'icon-allow-overlap': true,
        },
      },
    ];
    setSources(sources, therm_store.map);
    setLayers(layers, therm_store.map);

    if (therm_store.last_selected_project)
      await initial_project_initialization();
    if (therm_store.selected_features?.length) {
      styleSelection(therm_store.selected_features, {
        mapbox_instance: therm_store.map,
        colors_map_accessor: 'featureTypeId',
        colors_map: therm_store.ftg,
      });
    }
  }
});

const { addIconMarkerForItemCreation, removeItemCreationIcon } = useMapItemCreation({}, (e) => {
  if (task_marker_active.value) {
    if (therm_store.selected_features.length && therm_store.use_tasks) {
      createThermTasks(therm_store.selected_features);
    }
    else {
      open_task_popup.value = true;
    }
  }
  else if (form_marker_active.value) {
    open_form_popup.value = true;
  }
  task_form_centroid.value = e.lngLat;
});
async function initial_project_initialization() {
  const initial = async (project) => {
    therm_store.set_project_ortho({ project });
    if (!route.query?.metadata)
      await therm_store.fly_to_first_feature({ project });
    therm_store.update_map_features();
    therm_store.set_ftg_and_update_features_styles();
    addSymbols();
  };

  therm_store.active_projects?.forEach(async (project) => {
    if (
      project.uid !== therm_store.last_selected_project?.uid
      && project.is_active
    ) {
      await initial(project);
    }
  });
  await initial(therm_store.last_selected_project);
}

onMounted(async () => {
  await loadMapBoxPackage();

  await addMapboxToken();
  therm_store.map = await initMapbox({
    container_id: 'therm-map',
    style: 'mapbox://styles/mapbox/streets-v11',
  });
  loadDefectStatusIcons();
});

onUnmounted(() => {
  removeMapboxInstance(therm_store.map);
});

function toggleCreation(trigger_popup = true) {
  if (trigger_popup && therm_store.selected_features.length) {
    if (therm_store.use_tasks)
      createThermTasks(therm_store.selected_features);
    else
      open_task_popup.value = true;
  }
  else {
    task_marker_active.value = !task_marker_active.value;
    if (!task_marker_active.value) {
      removeItemCreationIcon(therm_store.map);
      open_task_popup.value = false;
      return;
    }

    addIconMarkerForItemCreation({
      map_instance: therm_store.map,
      icon: 'status-1',
    });
  }
}

function toggleCreateChooser() {
  active_tool.value = active_tool.value === 'create' ? '' : 'create';
}

function onCreateTask() {
  active_tool.value = '';
  toggleCreation();
}

function onCreateForm() {
  if (!therm_store.selected_features.length) {
    active_tool.value = '';
    $toast({
      title: $t('No features selected'),
      text: $t('Please select at least one feature to create a form'),
      type: 'error',
    });
    return;
  }
  toggleCreationForm();
}

function toggleCreationForm(trigger_popup = true) {
  active_tool.value = '';
  if (trigger_popup && therm_store.selected_features.length) {
    open_form_popup.value = true;
    return;
  }
  form_marker_active.value = !form_marker_active.value;
  if (!form_marker_active.value) {
    removeItemCreationIcon(therm_store.map);
    open_form_popup.value = false;
    return;
  }
  addIconMarkerForItemCreation({
    map_instance: therm_store.map,
    icon: 'open-form',
  });
}

function closeFormPopup() {
  open_form_popup.value = false;
  if (form_marker_active.value) {
    form_marker_active.value = false;
    removeItemCreationIcon(therm_store.map);
  }
}

async function onToolActivated(tool_event_name) {
  switch (tool_event_name) {
    case 'zoom-in':
      therm_store.map.zoomIn();
      break;
    case 'zoom-out':
      therm_store.map.zoomOut();
      break;
    case 'reset-north':
      await therm_store.fly_to_project({
        project:
                therm_store.active_project
                ?? therm_store.last_selected_project,
      });
      break;
    default:
      break;
  }
}
</script>

<template>
  <div>
    <div class="flex m-0 overflow-hidden h-full">
      <div class="flex-grow relative">
        <ThermProjects class="therm-projects top-3" />
        <ThermLayers />
        <div id="therm-map" />
        <ThermTools
          :active_tool="active_tool"
          :active_tools="{
            create: task_marker_active || form_marker_active,
          }"
          @tool-activated="onToolActivated"
          @compare="$router.push({ name: 'therm-compare', params: { ...route.params } })"
          @create="toggleCreateChooser"
          @create-task="onCreateTask"
          @create-form="onCreateForm"
        />
        <TaskForm
          v-if="open_task_popup" :marker_location="task_form_centroid"
          @refresh-symbols="addSymbols(), toggleCreation(false)"
          @toggle-creation="toggleCreation"
          @close="open_task_popup = false"
        />
        <ThermNewForm
          v-if="open_form_popup"
          :marker_location="task_form_centroid"
          opened_from="Direct"
          @close="closeFormPopup"
          @form-created="closeFormPopup"
        />
      </div>
      <ThermRightSidebar class="flex-grow p-0" />
    </div>
  </div>
</template>
